import type { ReactElement } from 'react';
import {
    CardDetailsModel,
    CardDetailsModelSchema,
    IPG_CARD_DETAILS_MODEL_POLICIES,
    type IpgSystemId,
} from '@pillar-portal/api-types';
import { Label } from '@/components/ui/label';

export interface CardDetailsModelControlProps {
    readonly ipgSystemId: IpgSystemId;
    readonly value: CardDetailsModel;
    readonly onChange: (value: CardDetailsModel) => void;
}

const CardDetailsModels: readonly CardDetailsModel[] = [
    CardDetailsModel.FULLY_OWNED,
    CardDetailsModel.HOSTED_IFRAME,
    CardDetailsModel.HOSTED_FIELDS,
];

function formatCardDetailsModelLabel(model: CardDetailsModel): string {
    switch (model) {
        case CardDetailsModel.FULLY_OWNED:
            return 'Anchorbase card form';
        case CardDetailsModel.HOSTED_IFRAME:
            return 'Payment provider secure form';
        case CardDetailsModel.HOSTED_FIELDS:
            return 'Payment provider secure fields';
        default: {
            const exhaustiveModel: never = model;
            throw new Error(`Unknown card-details model: ${exhaustiveModel}`);
        }
    }
}

export function CardDetailsModelControl({
    ipgSystemId,
    value,
    onChange,
}: CardDetailsModelControlProps): ReactElement {
    const policy = IPG_CARD_DETAILS_MODEL_POLICIES[ipgSystemId];

    return (
        <div className="space-y-2">
            <Label htmlFor={`card-details-model-${ipgSystemId}`}>Card entry form</Label>
            <select
                id={`card-details-model-${ipgSystemId}`}
                className="w-full rounded border px-3 py-2"
                value={value}
                onChange={(event) => onChange(CardDetailsModelSchema.parse(event.target.value))}
            >
                {CardDetailsModels.map((model) => {
                    const capable = policy.capable_models.includes(model);
                    const enabled = policy.enabled_models.includes(model);
                    return (
                        <option key={model} value={model} disabled={!enabled}>
                            {formatCardDetailsModelLabel(model)}
                            {enabled ? '' : capable ? ' (unavailable)' : ' (unsupported)'}
                        </option>
                    );
                })}
            </select>
        </div>
    );
}
